import API from "../api";
import { UploadedPart } from "../api/scenes";

interface ChunkedUploadOptions {
  threadsQuantity?: number;
  file: File;
  fileName: string;
  chunkSize?: number;
  sceneId: string;
  abortSignal: AbortSignal;
  onComplete: () => void;
  onError: () => void;
  onProgress: (chunkId: string, e: ProgressEvent<XMLHttpRequestEventTarget>) => void;
}

type Part = {
  url: string;
  partNumber: number;
}

export class ChunkedUploader {
  threadsQuantity: number;
  chunkSize: number;
  file: File;
  fileName: string;
  sceneId: string;
  abortSignal: AbortSignal;
  uploadId: string;
  parts: Part[];
  uploadedParts: UploadedPart[];
  activeConnections: { [key: string]: AbortController};
  onComplete: () => void;
  onError: () => void;
  onProgress: (chunkId: string, e: ProgressEvent<XMLHttpRequestEventTarget>) => void;

  constructor(
    {
      threadsQuantity = 5,
      chunkSize = 0,
      file,
      fileName,
      sceneId,
      abortSignal,
      onComplete,
      onError,
      onProgress,
    }: ChunkedUploadOptions
  ) {
    this.threadsQuantity = Math.min(threadsQuantity, 15);
    this.chunkSize = Math.max((1024 * 1024 * chunkSize), (1024 * 1024 * 5));
    this.file = file;
    this.fileName = fileName;
    this.sceneId = sceneId;
    this.abortSignal = abortSignal;
    this.onComplete = onComplete;
    this.onError = onError;
    this.onProgress = onProgress;

    this.parts = [];
    this.uploadedParts = [];
    this.activeConnections = {};
    this.uploadId = "";
  }

  start() {
    this.initialize();
  }

  async initialize() {
    const numberOfParts = Math.ceil(this.file.size / this.chunkSize);
    const initResponse = await API.initializeUpload(this.sceneId, this.fileName, numberOfParts);

    this.uploadId = initResponse.upload_id;
    this.parts = initResponse.urls.map((url, index) => ({ url, partNumber: index + 1 }));
    this.uploadedParts = [];
    this.activeConnections = {};

    this.abortSignal.addEventListener("abort", () => this.abort());
    this.sendNext();
  }

  sendNext(retry = 0) {
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      // Do not start new chunk upload if we maxed out the number of parallel uploads
      return;
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete();
      }

      return;
    }

    const part = this.parts.pop();
    if (this.file && part) {
      const sentSize = (part.partNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);
      const sendChunkStarted = () => this.sendNext();

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          delete this.activeConnections[part.partNumber];
          this.sendNext();
        })
        .catch((error) => {
          if (retry < 6) {
            retry++;
            const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

            // exponential backoff retry before giving up
            wait(2 ** retry * 100).then(() => {
              this.parts.push(part);
              this.sendNext(retry);
            });
          } else {
            // eslint-disable-next-line no-console
            console.log(`Part#${part.partNumber} failed to upload, giving up`);
            this.complete(error);
          }
        });

    }
  }

  sendChunk(chunk: Blob, part: Part, sendChunkStarted: () => void) {
    const onProgress = (e: ProgressEvent<XMLHttpRequestEventTarget>) => this.onProgress(part.url, e);
    const abortController = new AbortController();
    this.activeConnections[part.partNumber] = abortController;
    const upload = API.uploadFile(part.url, chunk, abortController.signal, onProgress)
      .then(response => {
        const eTag = response.headers.get("ETag");
        if (eTag) {
          const uploadedPart = {
            PartNumber: part.partNumber,
            ETag: eTag.replaceAll("\"", ""),
          };
          this.uploadedParts.push(uploadedPart);
        }
      });
    sendChunkStarted();
    return upload;
  }

  complete(error?: Error) {
    if (error) {
      this.abort();
      this.onError();
      return;
    }
    API.completeUpload(this.sceneId, this.fileName, { upload_id: this.uploadId, parts: this.uploadedParts })
      .then(this.onComplete)
      .catch(this.onError);
  }

  abort() {
    Object.keys(this.activeConnections).map((key) => this.activeConnections[key].abort());
  }
}
